import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { debounce, mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    super.connectedCallback();

    // One page form owns history restoration; the drawer shares its section.
    if (!this.closest('theme-drawer')) window.addEventListener('popstate', this.#restoreHistory);
    // In vertical filter mode the in-page filters take over at ≥750px, so a
    // drawer left open across a mobile→desktop resize would render duplicate
    // filter UI. `data-filter-style` is only set on the drawer variant (see
    // blocks/filters.liquid), so matching "vertical" here uniquely targets it.
    if (this.dataset.filterStyle === 'vertical') {
      mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#restoreHistory);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  #restoreHistory = () => this.#updateSection();

  #onBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (!event.matches) return;
    const drawer = document.getElementById('filters-drawer');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  };

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');
    const sortBy = newParameters.get('sort_by');
    if (sortBy) newParameters.set('sort_by', sortBy);

    if (this.dataset.pageType === 'search') {
      const currentParameters = new URLSearchParams(location.search);
      for (const key of ['type', 'options[prefix]']) {
        if (currentParameters.has(key)) newParameters.set(key, currentParameters.get(key) ?? '');
      }
    }

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  };

  /**
   * Updates the section
   * @returns {Promise<void>}
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');
    const renderSection = () =>
      sectionRenderer.renderSection(this.sectionId).then(() => {
        document.dispatchEvent(new Event('facets:section-rendered'));
      });

    if (viewTransition) {
      return startViewTransition(renderSection, ['product-grid']);
    } else {
      return renderSection();
    }
  }

  /**
   * Dispatches search:update event if on the search page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchSearchUpdateEvent(renderPromise) {
    const query = this.#getSearchQuery();

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = SearchUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: SearchUpdateEvent.getSortKey(),
        },
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with result count
    renderPromise
      .then(() => deferredPromise.resolve({ totalCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }

  /**
   * Gets the count of product results from the server-rendered data attribute.
   * @returns {number}
   */
  #getResultsCount() {
    return Number(this.dataset.resultsCount) || 0;
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    const targetURL = new URL(url, location.href);
    if (this.dataset.pageType === 'search') {
      const currentParameters = new URLSearchParams(location.search);
      for (const key of ['type', 'options[prefix]']) {
        if (currentParameters.has(key)) targetURL.searchParams.set(key, currentParameters.get(key) ?? '');
      }
    }
    history.pushState('', '', targetURL);
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  }

  /**
   * Dispatches collection:update event if on a collection page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchCollectionUpdateEvent(renderPromise) {
    // Build collection object with available identifiers
    // Only use numeric IDs — non-numeric values like "all" fail GID validation
    /** @type {any} */
    const rawId = this.dataset.collectionId;
    const collectionData = {
      id: rawId && /^\d+$/.test(rawId) ? rawId : null,
      handle: /** @type {string} */ (this.dataset.collectionHandle),
      productsCount: this.#getResultsCount(),
    };

    const deferredPromise = CollectionUpdateEvent.createPromise();
    const productFilters = CollectionUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new CollectionUpdateEvent({
        collection: collectionData,
        ...(productFilters && { productFilters }),
        sortKey: CollectionUpdateEvent.getSortKey(),
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with product count
    renderPromise
      .then(() => deferredPromise.resolve({ productsCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Fires immediately on pointerdown so a quick tap triggers the prefetch.
   * @param {PointerEvent} event
   */
  prefetchPageImmediate(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }

  /**
   * Reads `checked` at call time, so callers must invoke it before the toggle
   * for the predicted post-toggle URL to be correct.
   * @param {HTMLElement} optionElement
   */
  #prefetchOption(optionElement) {
    const form = this.closest('form');
    if (!form) return;

    const inputElement = optionElement.querySelector('input');
    if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const formData = new FormData(form);
    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }

  /**
   * @param {PointerEvent} event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
  }

  /**
   * Preserves digits still being typed when another price input refreshes this
   * server-rendered section. This is Horizon 4.2's focused-input contract.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    if (event.target instanceof HTMLInputElement) event.target.setAttribute('data-skip-value-update', 'true');
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.target instanceof HTMLInputElement) event.target.removeAttribute('data-skip-value-update');
  };

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

const CACAO_RANGE_VALUES_BY_PARAM = new Map();
const CACAO_RANGE_STORAGE_PREFIX = 'cacao-range-values-v2';

class CacaoRange extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.nativeInputs = Array.from(this.querySelectorAll('.cacao-range__native-input'));
    this.paramName = this.dataset.param || this.nativeInputs[0]?.name || '';
    this.values = this.getStableValues();

    if (!this.nativeInputs.length || !this.values.length) return;

    this.ensureNativeInputs();
    this.nativeInputs = Array.from(this.querySelectorAll('.cacao-range__native-input'));

    this.lastIndex = this.values.length - 1;

    this.stopPropagation = this.stopPropagation.bind(this);
    this.handleRangeInput = this.handleRangeInput.bind(this);
    this.handleRangeChange = this.handleRangeChange.bind(this);
    this.handleNumberInput = this.handleNumberInput.bind(this);
    this.handleNumberChange = this.handleNumberChange.bind(this);
    this.refreshFromCurrentURL = this.refreshFromCurrentURL.bind(this);

    if (!this.bindControlElements()) return;

    this.inputMin.min = this.values[0].toString();
    this.inputMin.max = this.values[this.lastIndex].toString();
    this.inputMax.min = this.values[0].toString();
    this.inputMax.max = this.values[this.lastIndex].toString();

    this.rangeMin.min = '0';
    this.rangeMin.max = this.lastIndex.toString();
    this.rangeMax.min = '0';
    this.rangeMax.max = this.lastIndex.toString();

    this.syncNativeInputsFromURL();
    this.syncFromNativeInputs();

    document.addEventListener(StandardEvents.collectionUpdate, this.refreshFromCurrentURL);
    document.addEventListener(StandardEvents.searchUpdate, this.refreshFromCurrentURL);
    document.addEventListener('facets:section-rendered', this.refreshFromCurrentURL);
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.unbindControlElements();
    document.removeEventListener(StandardEvents.collectionUpdate, this.refreshFromCurrentURL);
    document.removeEventListener(StandardEvents.searchUpdate, this.refreshFromCurrentURL);
    document.removeEventListener('facets:section-rendered', this.refreshFromCurrentURL);
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  get storageKey() {
    return `${CACAO_RANGE_STORAGE_PREFIX}:${window.location.pathname}:${this.paramName}`;
  }

  getCurrentURLValues() {
    if (!this.paramName) return [];

    return new URL(window.location.href).searchParams
      .getAll(this.paramName)
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  }

  getStoredValues() {
    const memoryValues = CACAO_RANGE_VALUES_BY_PARAM.get(this.storageKey) ?? [];

    try {
      const storedValues = JSON.parse(sessionStorage.getItem(this.storageKey) ?? '[]');
      if (Array.isArray(storedValues)) {
        return [...memoryValues, ...storedValues.map((value) => Number(value)).filter((value) => !Number.isNaN(value))];
      }
    } catch (_error) {
      return memoryValues;
    }

    return memoryValues;
  }

  getDiscoveredValues() {
    const inputValues = Array.from(this.querySelectorAll('.cacao-range__native-input'))
      .map((input) => Number(input.value))
      .filter((value) => !Number.isNaN(value));

    return [...inputValues, ...this.getCurrentURLValues()];
  }

  getStableValues() {
    const values = [...new Set([...this.getStoredValues(), ...this.getDiscoveredValues()])].sort((a, b) => a - b);

    if (values.length) {
      CACAO_RANGE_VALUES_BY_PARAM.set(this.storageKey, values);
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(values));
      } catch (_error) {
        void _error;
      }
    }

    return values;
  }

  ensureNativeInputs() {
    const nativeContainer = this.querySelector('.cacao-range__native');
    if (!(nativeContainer instanceof HTMLElement) || !this.paramName) return;

    const existingInputs = new Map(
      Array.from(nativeContainer.querySelectorAll('.cacao-range__native-input')).map((input) => [input.value, input])
    );

    this.values.forEach((value) => {
      const stringValue = value.toString();
      let input = existingInputs.get(stringValue);

      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'cacao-range__native-input';
        input.value = stringValue;
        input.dataset.label = stringValue;
        nativeContainer.append(input);
      }

      input.name = this.paramName;
    });
  }

  syncNativeInputsFromURL() {
    if (!this.paramName) return;

    const activeValues = new Set(new URL(window.location.href).searchParams.getAll(this.paramName));

    this.nativeInputs.forEach((input) => {
      input.checked = activeValues.has(input.value);
    });
  }

  bindControlElements() {
    this.unbindControlElements();

    this.inputMin = this.querySelector('.input-min');
    this.inputMax = this.querySelector('.input-max');
    this.rangeMin = this.querySelector('.range-min');
    this.rangeMax = this.querySelector('.range-max');
    this.progress = this.querySelector('.cacao-range__progress');

    if (
      !(this.inputMin instanceof HTMLInputElement) ||
      !(this.inputMax instanceof HTMLInputElement) ||
      !(this.rangeMin instanceof HTMLInputElement) ||
      !(this.rangeMax instanceof HTMLInputElement) ||
      !(this.progress instanceof HTMLElement)
    ) {
      return false;
    }

    this.inputMin.addEventListener('input', this.handleNumberInput);
    this.inputMax.addEventListener('input', this.handleNumberInput);
    this.inputMin.addEventListener('change', this.handleNumberChange);
    this.inputMax.addEventListener('change', this.handleNumberChange);
    this.rangeMin.addEventListener('input', this.handleRangeInput);
    this.rangeMax.addEventListener('input', this.handleRangeInput);
    this.rangeMin.addEventListener('change', this.handleRangeChange);
    this.rangeMax.addEventListener('change', this.handleRangeChange);

    return true;
  }

  unbindControlElements() {
    this.inputMin?.removeEventListener('input', this.handleNumberInput);
    this.inputMax?.removeEventListener('input', this.handleNumberInput);
    this.inputMin?.removeEventListener('change', this.handleNumberChange);
    this.inputMax?.removeEventListener('change', this.handleNumberChange);
    this.rangeMin?.removeEventListener('input', this.handleRangeInput);
    this.rangeMax?.removeEventListener('input', this.handleRangeInput);
    this.rangeMin?.removeEventListener('change', this.handleRangeChange);
    this.rangeMax?.removeEventListener('change', this.handleRangeChange);
  }

  refreshFromCurrentURL() {
    if (!this.initialized) return;

    this.isSyncingDom = true;
    this.nativeInputs = Array.from(this.querySelectorAll('.cacao-range__native-input'));
    this.values = this.getStableValues();
    if (!this.nativeInputs.length || !this.values.length) {
      this.isSyncingDom = false;
      return;
    }

    this.ensureNativeInputs();
    this.nativeInputs = Array.from(this.querySelectorAll('.cacao-range__native-input'));
    this.lastIndex = this.values.length - 1;
    if (!this.bindControlElements()) {
      this.isSyncingDom = false;
      return;
    }

    this.inputMin.min = this.values[0].toString();
    this.inputMin.max = this.values[this.lastIndex].toString();
    this.inputMax.min = this.values[0].toString();
    this.inputMax.max = this.values[this.lastIndex].toString();
    this.rangeMin.min = '0';
    this.rangeMin.max = this.lastIndex.toString();
    this.rangeMax.min = '0';
    this.rangeMax.max = this.lastIndex.toString();
    this.syncNativeInputsFromURL();
    this.syncFromNativeInputs();
    this.isSyncingDom = false;
  }

  syncFromNativeInputs() {
    const activeValues = this.nativeInputs
      .filter((input) => input.checked)
      .map((input) => Number(input.value))
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b);

    if (!activeValues.length) {
      this.minIndex = 0;
      this.maxIndex = this.lastIndex;
    } else {
      this.minIndex = this.valueToIndex(activeValues[0], 'min');
      this.maxIndex = this.valueToIndex(activeValues[activeValues.length - 1], 'max');
    }

    this.render();
  }

  clearSelection() {
    this.minIndex = 0;
    this.maxIndex = this.lastIndex;
    this.render();
    this.commitSelection(true);
  }

  valueToIndex(value, side) {
    const exactIndex = this.values.indexOf(value);
    if (exactIndex !== -1) return exactIndex;

    if (side === 'min') {
      const nextIndex = this.values.findIndex((candidate) => candidate >= value);
      return nextIndex === -1 ? this.lastIndex : nextIndex;
    }

    for (let index = this.lastIndex; index >= 0; index -= 1) {
      if (this.values[index] <= value) return index;
    }

    return 0;
  }

  render() {
    if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax || !this.progress) return;

    this.rangeMin.value = this.minIndex.toString();
    this.rangeMax.value = this.maxIndex.toString();
    this.inputMin.value = this.values[this.minIndex].toString();
    this.inputMax.value = this.values[this.maxIndex].toString();
    this.updateProgress();
    this.updateSummary();
  }

  updateProgress() {
    if (!this.progress) return;

    const totalSteps = Math.max(1, this.lastIndex);
    const minPercent = (this.minIndex / totalSteps) * 100;
    const maxPercent = (this.maxIndex / totalSteps) * 100;

    this.progress.style.left = `${minPercent}%`;
    this.progress.style.right = `${100 - maxPercent}%`;
  }

  updateSummary() {
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');
    if (!statusComponent || typeof statusComponent.updateCacaoRangeSummary !== 'function') return;

    const isDefaultRange = this.minIndex === 0 && this.maxIndex === this.lastIndex;
    statusComponent.updateCacaoRangeSummary(
      this.values[this.minIndex],
      this.values[this.maxIndex],
      !isDefaultRange
    );
  }

  handleRangeInput(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.stopPropagation(event);

    const currentIndex = Number(event.target.value);

    if (event.target === this.rangeMin) {
      this.minIndex = Math.min(currentIndex, this.maxIndex);
    } else {
      this.maxIndex = Math.max(currentIndex, this.minIndex);
    }

    this.render();
  }

  handleRangeChange(event) {
    this.handleRangeInput(event);
    this.commitSelection();
  }

  handleNumberInput(event) {
    this.stopPropagation(event);
  }

  handleNumberChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.stopPropagation(event);

    const rawValue = Number(event.target.value);
    if (Number.isNaN(rawValue)) {
      this.render();
      return;
    }

    if (event.target === this.inputMin) {
      this.minIndex = this.valueToIndex(rawValue, 'min');
      this.minIndex = Math.min(this.minIndex, this.maxIndex);
    } else {
      this.maxIndex = this.valueToIndex(rawValue, 'max');
      this.maxIndex = Math.max(this.maxIndex, this.minIndex);
    }

    this.render();
    this.commitSelection();
  }

  commitSelection(force = false) {
    const selectedMin = this.values[this.minIndex];
    const selectedMax = this.values[this.maxIndex];
    const useDefaultRange = this.minIndex === 0 && this.maxIndex === this.lastIndex;
    let hasChanged = force;

    this.nativeInputs.forEach((input) => {
      const numericValue = Number(input.value);
      const shouldBeChecked =
        !useDefaultRange && !Number.isNaN(numericValue) && numericValue >= selectedMin && numericValue <= selectedMax;

      if (input.checked !== shouldBeChecked) {
        input.checked = shouldBeChecked;
        hasChanged = true;
      }
    });

    this.updateSummary();

    if (!hasChanged) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }
}

if (!customElements.get('cacao-range')) {
  customElements.define('cacao-range', CacaoRange);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component, cacao-range');

    if (container instanceof CacaoRange) {
      container.clearSelection();
      return;
    }

    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        if (input.type === 'checkbox') input.checked = false;
        else input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle('facets__clear--active', filters.length > 0);
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle(activeClass, filters.length > 0);
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        // Component delegation presents the component as event.target.
        // Keyboard selection needs the actual focused option in the event path.
        if (event.composedPath()[0] instanceof Element) {
          const targetOption = /** @type {Element} */ (event.composedPath()[0]).closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `<bdi>${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}</bdi>`;
  }

  /**
   * Updates the cacao range summary
   * @param {number} minValue - The minimum cacao value
   * @param {number} maxValue - The maximum cacao value
   * @param {boolean} isActive - Whether the range is active
   */
  updateCacaoRangeSummary(minValue, maxValue, isActive) {
    const { facetStatus } = this.refs;

    if (!isActive) {
      facetStatus.innerHTML = '';
      return;
    }

    facetStatus.innerHTML = `${minValue} - ${maxValue}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

class FacetScrollPanel {
  /**
   * @param {HTMLElement} shell
   */
  constructor(shell) {
    this.shell = shell;
    this.scrollable = shell.querySelector('[data-facet-scrollable]');
    this.upButton = shell.querySelector('[data-scroll-direction="up"]');
    this.downButton = shell.querySelector('[data-scroll-direction="down"]');
    this.details = shell.closest('details');

    if (!(this.scrollable instanceof HTMLElement)) return;
    if (!(this.upButton instanceof HTMLButtonElement)) return;
    if (!(this.downButton instanceof HTMLButtonElement)) return;

    this.onScroll = this.onScroll.bind(this);
    this.onDetailsToggle = this.onDetailsToggle.bind(this);
    this.onResize = this.onResize.bind(this);

    this.scrollable.addEventListener('scroll', this.onScroll, { passive: true });
    this.upButton.addEventListener('click', () => this.scrollBy('up'));
    this.downButton.addEventListener('click', () => this.scrollBy('down'));
    this.details?.addEventListener('toggle', this.onDetailsToggle);
    window.addEventListener('resize', this.onResize, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.scrollable);

    requestAnimationFrame(() => this.update());
  }

  onScroll() {
    this.update();
  }

  onDetailsToggle() {
    requestAnimationFrame(() => this.update());
  }

  onResize() {
    this.update();
  }

  scrollBy(direction) {
    if (!(this.scrollable instanceof HTMLElement)) return;

    const amount = Math.max(120, this.scrollable.clientHeight * 0.6) * (direction === 'down' ? 1 : -1);
    this.scrollable.scrollBy({ top: amount, behavior: 'smooth' });
  }

  update() {
    if (!(this.scrollable instanceof HTMLElement)) return;
    if (!(this.upButton instanceof HTMLButtonElement)) return;
    if (!(this.downButton instanceof HTMLButtonElement)) return;

    const canScroll = this.scrollable.scrollHeight - this.scrollable.clientHeight > 2;
    const atTop = this.scrollable.scrollTop <= 1;
    const atBottom = this.scrollable.scrollTop + this.scrollable.clientHeight >= this.scrollable.scrollHeight - 1;

    this.upButton.hidden = !canScroll || atTop;
    this.downButton.hidden = !canScroll || atBottom;
  }
}

const initFacetScrollPanels = (root = document) => {
  root.querySelectorAll('[data-facet-scroll-shell]').forEach((shell) => {
    if (!(shell instanceof HTMLElement)) return;
    if (shell.dataset.facetScrollReady === 'true') return;

    shell.dataset.facetScrollReady = 'true';
    new FacetScrollPanel(shell);
  });
};

document.addEventListener('DOMContentLoaded', () => initFacetScrollPanels());
document.addEventListener('facets:section-rendered', () => initFacetScrollPanels());

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
